// src/components/profile/DeleteAccount.js
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Divider,
  Card,
  CardContent,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Chip,
  Grid
} from '@mui/material';
import {
  DeleteForever as DeleteForeverIcon,
  Warning as WarningIcon,
  Security as SecurityIcon,
  Backup as BackupIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Download as DownloadIcon,
  Email as EmailIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const DeleteAccount = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [confirmationStep, setConfirmationStep] = useState(0);
  const [formData, setFormData] = useState({
    confirmEmail: '',
    confirmText: '',
    reason: '',
    password: '',
    dataExport: false,
    emailConfirmation: false,
    finalConfirmation: false
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  const steps = [
    {
      label: 'Información importante',
      description: 'Comprende qué sucederá con tu cuenta'
    },
    {
      label: 'Exportar datos',
      description: 'Descarga tus datos antes de eliminar'
    },
    {
      label: 'Verificación',
      description: 'Confirma tu identidad'
    },
    {
      label: 'Confirmación final',
      description: 'Última oportunidad para cancelar'
    }
  ];

  const dataCategories = [
    { name: 'Información personal', description: 'Nombre, email, teléfono' },
    { name: 'Datos de organización', description: 'Información de tu club/equipo' },
    { name: 'Historial de eventos', description: 'Participaciones y resultados' },
    { name: 'Configuraciones', description: 'Preferencias y configuraciones' },
    { name: 'Archivos subidos', description: 'Documentos y fotos' }
  ];

  const consequencesOfDeletion = [
    'Tu cuenta será eliminada permanentemente',
    'Perderás acceso a todos los datos y configuraciones',
    'No podrás recuperar la información una vez eliminada',
    'Las suscripciones activas serán canceladas',
    'Los datos de facturación serán eliminados',
    'Tu organización será desvinculada (si aplica)',
    'Los eventos en los que participaste mantendrán tus resultados de forma anónima'
  ];

  const handleInputChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleCheckboxChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.checked
    }));
  };

  const handleNext = () => {
    setConfirmationStep(prev => prev + 1);
  };

  const handleBack = () => {
    setConfirmationStep(prev => prev - 1);
  };

  const handleExportData = () => {
    // Simular exportación de datos
    setMessage({ 
      type: 'info', 
      text: 'Preparando exportación de datos. Recibirás un email cuando esté listo.' 
    });
    
    setTimeout(() => {
      setFormData(prev => ({
        ...prev,
        dataExport: true
      }));
      setMessage({ 
        type: 'success', 
        text: 'Datos exportados correctamente. Revisa tu email.' 
      });
    }, 2000);
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        return true; // Solo información
      case 2:
        return formData.dataExport; // Debe exportar datos o marcar que no quiere
      case 3:
        return (
          formData.confirmEmail === user?.email &&
          formData.confirmText === 'ELIMINAR MI CUENTA' &&
          formData.password.length > 0
        );
      case 4:
        return (
          formData.emailConfirmation &&
          formData.finalConfirmation &&
          formData.reason.trim().length > 0
        );
      default:
        return false;
    }
  };

  const handleDeleteAccount = async () => {
    setLoading(true);
    
    try {
      // Aquí irías a tu API para eliminar la cuenta
      // Incluiría:
      // 1. Verificar la contraseña
      // 2. Eliminar datos de Firestore
      // 3. Cancelar suscripciones de Stripe
      // 4. Eliminar cuenta de Firebase Auth
      
      // Simulación
      setTimeout(async () => {
        await logout();
        navigate('/auth/login', { 
          state: { 
            message: 'Tu cuenta ha sido eliminada exitosamente.' 
          } 
        });
      }, 3000);
      
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: 'Error al eliminar la cuenta. Intenta nuevamente.' 
      });
      setLoading(false);
    }
  };

  const canProceedToNext = () => {
    return validateStep(confirmationStep + 1);
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Alert severity="error" sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                ⚠️ Esta acción es irreversible
              </Typography>
              <Typography variant="body2">
                Una vez que elimines tu cuenta, no podrás recuperar ninguna información.
              </Typography>
            </Alert>

            <Typography variant="h6" gutterBottom>
              ¿Qué sucederá cuando elimines tu cuenta?
            </Typography>
            
            <List>
              {consequencesOfDeletion.map((consequence, index) => (
                <ListItem key={index}>
                  <ListItemIcon>
                    <WarningIcon color="error" />
                  </ListItemIcon>
                  <ListItemText primary={consequence} />
                </ListItem>
              ))}
            </List>
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Exportar tus datos
            </Typography>
            
            <Alert severity="info" sx={{ mb: 3 }}>
              Te recomendamos exportar tus datos antes de eliminar tu cuenta. 
              Esta es tu última oportunidad para conservar tu información.
            </Alert>

            <Typography variant="subtitle1" gutterBottom>
              Datos que puedes exportar:
            </Typography>
            
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {dataCategories.map((category, index) => (
                <Grid item xs={12} sm={6} key={index}>
                  <Card variant="outlined">
                    <CardContent sx={{ py: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        {category.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {category.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            <Box display="flex" gap={2}>
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={handleExportData}
                disabled={formData.dataExport}
              >
                {formData.dataExport ? 'Datos Exportados' : 'Exportar Mis Datos'}
              </Button>
              
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!formData.dataExport}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      dataExport: !e.target.checked
                    }))}
                  />
                }
                label="No quiero exportar mis datos"
              />
            </Box>
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Verificación de identidad
            </Typography>
            
            <Alert severity="warning" sx={{ mb: 3 }}>
              Para confirmar que realmente eres tú, necesitamos verificar tu identidad.
            </Alert>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Confirma tu email"
                  value={formData.confirmEmail}
                  onChange={handleInputChange('confirmEmail')}
                  helperText={`Escribe: ${user?.email}`}
                  error={formData.confirmEmail && formData.confirmEmail !== user?.email}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Escribe 'ELIMINAR MI CUENTA'"
                  value={formData.confirmText}
                  onChange={handleInputChange('confirmText')}
                  helperText="Escribe exactamente: ELIMINAR MI CUENTA"
                  error={formData.confirmText && formData.confirmText !== 'ELIMINAR MI CUENTA'}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Tu contraseña actual"
                  type="password"
                  value={formData.password}
                  onChange={handleInputChange('password')}
                  helperText="Confirma tu contraseña para verificar tu identidad"
                />
              </Grid>
            </Grid>
          </Box>
        );

      case 3:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Confirmación final
            </Typography>
            
            <Alert severity="error" sx={{ mb: 3 }}>
              <Typography variant="body1" fontWeight="bold">
                🚨 Última advertencia
              </Typography>
              <Typography variant="body2">
                Esta es tu última oportunidad para cancelar. Una vez que confirmes, 
                tu cuenta será eliminada inmediatamente.
              </Typography>
            </Alert>

            <TextField
              fullWidth
              multiline
              rows={3}
              label="¿Por qué eliminas tu cuenta? (opcional)"
              value={formData.reason}
              onChange={handleInputChange('reason')}
              placeholder="Ayúdanos a mejorar contándonos por qué decides irte..."
              sx={{ mb: 3 }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.emailConfirmation}
                  onChange={handleCheckboxChange('emailConfirmation')}
                  color="error"
                />
              }
              label="He exportado mis datos importantes y entiendo que no podré recuperarlos"
            />
            
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.finalConfirmation}
                  onChange={handleCheckboxChange('finalConfirmation')}
                  color="error"
                />
              }
              label="Entiendo que esta acción es irreversible y quiero eliminar mi cuenta permanentemente"
            />

            {formData.emailConfirmation && formData.finalConfirmation && (
              <Alert severity="error" sx={{ mt: 2 }}>
                Al hacer clic en "Eliminar cuenta definitivamente", tu cuenta será eliminada inmediatamente.
              </Alert>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom fontWeight="bold" color="error">
        Eliminar Cuenta
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Elimina permanentemente tu cuenta y todos los datos asociados
      </Typography>

      <Divider sx={{ mb: 3 }} />

      {/* Mensaje de estado */}
      {message && (
        <Alert severity={message.type} sx={{ mb: 3 }}>
          {message.text}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" mb={3}>
            <DeleteForeverIcon color="error" sx={{ mr: 2, fontSize: 32 }} />
            <Box>
              <Typography variant="h6" color="error">
                Zona de Peligro
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Esta acción no se puede deshacer
              </Typography>
            </Box>
          </Box>

          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body1" gutterBottom>
              <strong>Antes de eliminar tu cuenta, considera:</strong>
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="• ¿Hay problemas que podemos ayudarte a resolver?" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• ¿Prefieres desactivar temporalmente tu cuenta?" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• ¿Has exportado todos los datos importantes?" />
              </ListItem>
            </List>
          </Alert>

          <Box display="flex" gap={2} mb={3}>
            <Button
              variant="outlined"
              startIcon={<EmailIcon />}
              href="mailto:soporte@athletic.com"
            >
              Contactar Soporte
            </Button>
            
            <Button
              variant="outlined"
              startIcon={<InfoIcon />}
            >
              Desactivar Temporalmente
            </Button>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Typography variant="body1" gutterBottom>
            Si estás seguro de que quieres eliminar tu cuenta permanentemente:
          </Typography>

          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteForeverIcon />}
            onClick={() => setDeleteDialogOpen(true)}
          >
            Iniciar Proceso de Eliminación
          </Button>
        </CardContent>
      </Card>

      {/* Dialog de confirmación */}
      <Dialog 
        open={deleteDialogOpen} 
        onClose={() => setDeleteDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
        disableEscapeKeyDown
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <DeleteForeverIcon color="error" sx={{ mr: 1 }} />
            Eliminar Cuenta - Paso {confirmationStep + 1} de {steps.length}
          </Box>
        </DialogTitle>
        
        <DialogContent>
          <Stepper activeStep={confirmationStep} orientation="vertical">
            {steps.map((step, index) => (
              <Step key={step.label}>
                <StepLabel>{step.label}</StepLabel>
                <StepContent>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {step.description}
                  </Typography>
                  {renderStepContent(index)}
                </StepContent>
              </Step>
            ))}
          </Stepper>
        </DialogContent>

        <DialogActions>
          <Button 
            onClick={() => setDeleteDialogOpen(false)}
            disabled={loading}
          >
            Cancelar
          </Button>
          
          {confirmationStep > 0 && (
            <Button 
              onClick={handleBack}
              disabled={loading}
            >
              Atrás
            </Button>
          )}
          
          {confirmationStep < steps.length - 1 ? (
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={!canProceedToNext()}
            >
              Siguiente
            </Button>
          ) : (
            <Button
              variant="contained"
              color="error"
              onClick={handleDeleteAccount}
              disabled={!canProceedToNext() || loading}
              startIcon={<DeleteForeverIcon />}
            >
              {loading ? 'Eliminando...' : 'Eliminar Cuenta Definitivamente'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DeleteAccount;